import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import LotteryWeekSelector, {
  getInitialWeekStart,
  getWeekRange,
} from '../../../components/LotteryWeekSelector';
import { PAGE_CARD_SX } from '../../../theme/pageCardSx';
import FilterSelectorWithApp from '../../../components/FilterSelectorWithApp';
import RouletteContentUserStatusChart from '../../../components/pointhome/roulette/content/RouletteContentUserStatusChart';
import RouletteContentDailyCountChart from '../../../components/pointhome/roulette/content/RouletteContentDailyCountChart';
import RouletteContentDailyCountTable from '../../../components/pointhome/roulette/content/RouletteContentDailyCountTable';
import type { UserStatusDatum } from '../../../components/pointhome/roulette/content/RouletteContentUserStatusChart';
import type { DailyCountDatum } from '../../../components/pointhome/roulette/content/RouletteContentDailyCountChart';
import type { DailyCountTableRow } from '../../../components/pointhome/roulette/content/RouletteContentDailyCountTable';
import apiClient from '../../../utils/axiosClient';
import type { ContentsParticipationItem } from '../../../types/luckyLottery.types';

dayjs.locale('ko');

// ────────────────────────────────────────
// 상수
// ────────────────────────────────────────

const STORAGE_KEY = 'avatye.ops.lucky-lottery.ticket.contents.filter';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(value);

const KPI_CARD_SX = {
  backgroundColor: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderRadius: 3,
  p: 2.5,
  minHeight: 72,
} as const;

// 콘텐츠 탭 고정 노출 순서. 목록에 없는 라벨(RC.Category명)은 이 뒤에 한글순으로 개별 탭으로 노출하고,
// 백엔드가 카테고리를 못 찾아 내려준 리터럴 "RC"(미분류)는 맨 뒤에 배치한다.
const CONTENTS_LABEL_FIXED_ORDER = [
  '영상광고',
  '광고보기',
  '홈방문하기',
  '웰컴보상',
  '복권 스크래치',
];
const CONTENTS_LABEL_UNCLASSIFIED = 'RC';

const contentsLabelSortKey = (label: string): [number, number, string] => {
  const fixedIndex = CONTENTS_LABEL_FIXED_ORDER.indexOf(label);
  if (fixedIndex !== -1) return [0, fixedIndex, label];
  if (label === CONTENTS_LABEL_UNCLASSIFIED) return [2, 0, label];
  return [1, 0, label];
};

const sortContentsItems = (items: ContentsParticipationItem[]) =>
  [...items].sort((a, b) => {
    const [ga, ia, la] = contentsLabelSortKey(a.label);
    const [gb, ib, lb] = contentsLabelSortKey(b.label);
    if (ga !== gb) return ga - gb;
    if (ia !== ib) return ia - ib;
    return la.localeCompare(lb, 'ko');
  });

// 고정 5종 외의 라벨(RC.Category명 개별 + 백엔드 "RC" fallback)은 병합하지 않고 각자 탭으로 유지한다.
// 탭 표시 시 "[RC] {라벨}" 접두어만 붙여 RC로 분류된 콘텐츠임을 표시한다.
const mergeAndSortContentsData = (
  items: ContentsParticipationItem[],
): ContentsParticipationItem[] => sortContentsItems(items);

// ────────────────────────────────────────
// 필터 영속화 헬퍼
// ────────────────────────────────────────

type StoredFilter = {
  startDate: string;
  endDate: string;
  appName: string;
};

const getStoredFilter = (): StoredFilter | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored) as StoredFilter;
  } catch {
    // ignore
  }
  return null;
};

const saveFilter = (filter: StoredFilter) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filter));
  } catch {
    // ignore
  }
};

// ────────────────────────────────────────
// KPI 카드
// ────────────────────────────────────────

type KpiData = {
  totalDAU: number;
  totalTxnCount: number;
  totalUserCount: number;
  participationRate: number;
  avgTicket: number;
  totalRevenue: number;
};

function ContentsParticipationKpiCards({ data }: { data: KpiData | null }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
          lg: 'repeat(5, 1fr)',
        },
        gap: 2,
        mt: 3,
      }}
    >
      <Box sx={KPI_CARD_SX}>
        <Typography variant="body2" color="text.secondary" fontWeight="bold" mb={0.5}>
          활성 사용자 수
        </Typography>
        <Typography variant="h6" fontWeight="bold" color="#374151">
          {data ? `${data.totalDAU.toLocaleString('ko-KR')}명` : '—'}
        </Typography>
      </Box>

      <Box sx={KPI_CARD_SX}>
        <Typography variant="body2" color="text.secondary" fontWeight="bold" mb={0.5}>
          참여 완료 횟수 / 참여 사용자 수
        </Typography>
        <Typography variant="h6" fontWeight="bold" color="#374151">
          {data
            ? `${data.totalTxnCount.toLocaleString('ko-KR')}회 / ${data.totalUserCount.toLocaleString('ko-KR')}명`
            : '—'}
        </Typography>
      </Box>

      <Box sx={KPI_CARD_SX}>
        <Typography variant="body2" color="text.secondary" fontWeight="bold" mb={0.5}>
          참여율
        </Typography>
        <Typography variant="h6" fontWeight="bold" color="#374151">
          {data ? `${data.participationRate}%` : '—'}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: '#64748b' }}>
          참여 사용자 수 / 활성 사용자 수
        </Typography>
      </Box>

      <Box sx={KPI_CARD_SX}>
        <Typography variant="body2" color="text.secondary" fontWeight="bold" mb={0.5}>
          획득 평균 티켓
        </Typography>
        <Typography variant="h6" fontWeight="bold" color="#374151">
          {data ? `${data.avgTicket}티켓` : '—'}
        </Typography>
      </Box>

      <Box sx={KPI_CARD_SX}>
        <Typography variant="body2" color="text.secondary" fontWeight="bold" mb={0.5}>
          매출
        </Typography>
        <Typography variant="h6" fontWeight="bold" color="#374151">
          {data ? formatCurrency(data.totalRevenue) : '—'}
        </Typography>
      </Box>
    </Box>
  );
}

// ────────────────────────────────────────
// 페이지 컴포넌트
// ────────────────────────────────────────

export default function ContentsParticipation() {
  // ── 필터 초기값 (localStorage 복원)
  const stored = useMemo(() => getStoredFilter(), []);

  const initialWeek = useMemo(() => {
    if (stored?.startDate && stored?.endDate) {
      return { startDate: stored.startDate, endDate: stored.endDate };
    }
    const sunday = getInitialWeekStart();
    const { start, end } = getWeekRange(sunday);
    return { startDate: start.format('YYYY-MM-DD'), endDate: end.format('YYYY-MM-DD') };
  }, [stored]);

  const [startDate, setStartDate] = useState<string>(initialWeek.startDate);
  const [endDate, setEndDate] = useState<string>(initialWeek.endDate);
  const [appName, setAppName] = useState<string>(stored?.appName ?? '앱 전체');
  const [selectedOs, setSelectedOs] = useState<string>('all');

  // ── 앱 목록
  const [appOptions, setAppOptions] = useState<string[]>(['앱 전체']);

  // ── 데이터 상태
  const [data, setData] = useState<ContentsParticipationItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // ── 콘텐츠(sub_type) 탭
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);

  // ── 앱 목록 조회
  const fetchAppOptions = useCallback(() => {
    if (!startDate || !endDate) return;
    apiClient
      .get<string[]>('/api/v1/stat/lucky-lottery/app/list', { params: { start_date: startDate, end_date: endDate } })
      .then((res) => {
        setAppOptions(['앱 전체', ...(res.data || [])]);
      })
      .catch(() => undefined);
  }, [startDate, endDate]);

  // ── 데이터 조회 (sub_type 필터 없이 전체 콘텐츠를 한 번에 조회, 탭 분리는 프론트에서 처리)
  const fetchData = useCallback(() => {
    if (!startDate || !endDate) return;

    const app = appName === '앱 전체' ? null : appName;

    // 필터 저장
    saveFilter({ startDate, endDate, appName });

    setLoading(true);
    apiClient
      .get<ContentsParticipationItem[]>(
        '/api/v1/analytics/lucky-lottery/ticket/received/contents',
        {
          params: {
            start_date: startDate,
            end_date: endDate,
            ...(app ? { app_name: app } : {}),
            ...(selectedOs !== 'all' ? { os_type: selectedOs } : {}),
          },
        },
      )
      .then((res) => {
        setData(res.data);
        setErrorMessage('');
      })
      .catch(() => {
        setData(null);
        setErrorMessage('데이터 로드에 실패했습니다.');
      })
      .finally(() => setLoading(false));
  }, [startDate, endDate, appName, selectedOs]);

  useEffect(() => {
    fetchAppOptions();
  }, [fetchAppOptions]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── API는 label(RC.Category명 포함)별로 집계되어 있음. 고정 5종은 지정 순서로, 나머지는 각자 탭 유지한 채 한글순 정렬
  const mergedData = useMemo(() => mergeAndSortContentsData(data ?? []), [data]);
  const labels = useMemo(() => mergedData.map((item) => item.label), [mergedData]);

  // ── 탭 선택 상태 동기화 (데이터 변경 시 유효하지 않은 탭이면 첫 번째 label로 이동)
  useEffect(() => {
    if (labels.length === 0) {
      setSelectedLabel(null);
      return;
    }
    if (!selectedLabel || !labels.includes(selectedLabel)) {
      setSelectedLabel(labels[0]);
    }
  }, [labels, selectedLabel]);

  // ── 선택된 탭(label)의 집계 데이터
  const selectedItem = useMemo(
    () => mergedData.find((item) => item.label === selectedLabel) ?? null,
    [mergedData, selectedLabel],
  );

  // ── KPI 카드: 백엔드에서 이미 계산된 값을 그대로 전달
  const kpiData: KpiData | null = selectedItem;

  // ── 사용자 현황 차트: 필드명만 차트 컴포넌트 규격에 맞춰 매핑 (계산 없음)
  const userStatusChartData = useMemo((): UserStatusDatum[] => {
    return (selectedItem?.dailyUserStats ?? []).map((stat) => ({
      label: stat.date,
      dayLabel: dayjs(stat.date).format('MM/DD'),
      activeUsers: stat.dau,
      participationCompletedUsers: stat.userCount,
    }));
  }, [selectedItem]);

  // ── 일별 매출 차트: 백엔드가 dailyUserStats에 함께 내려주는 revenue를 날짜순으로 매핑
  const dailyRevenueChartData = useMemo(
    () =>
      (selectedItem?.dailyUserStats ?? []).map((stat) => ({
        dayLabel: dayjs(stat.date).format('MM/DD'),
        revenue: stat.revenue,
      })),
    [selectedItem],
  );

  // ── 일 참여횟수(n회) 구간 순서: 백엔드가 준 요약값의 키를 숫자순으로 정렬만 함
  const participationCountKeys = useMemo(() => {
    const summary = selectedItem?.participationCountSummary ?? {};
    return Object.keys(summary)
      .map(Number)
      .sort((a, b) => a - b);
  }, [selectedItem]);

  // ── 일 참여횟수 현황 차트: 백엔드 요약값을 차트 규격으로 매핑
  const dailyCountChartData = useMemo((): DailyCountDatum[] => {
    const summary = selectedItem?.participationCountSummary ?? {};
    return participationCountKeys.map((count) => ({
      range: `${count}회`,
      value: summary[String(count)] ?? 0,
    }));
  }, [participationCountKeys, selectedItem]);

  // ── 일 참여횟수 현황 상세 테이블: 백엔드가 이미 계산한 count/rate를 그대로 매핑, 매출은 dailyUserStats에서 날짜로 매칭
  const dailyCountTableRows = useMemo((): DailyCountTableRow[] => {
    const revenueByDate = new Map(
      (selectedItem?.dailyUserStats ?? []).map((stat) => [stat.date, stat.revenue]),
    );
    return (selectedItem?.dailyParticipationCount ?? []).map((entry) => ({
      date: entry.date,
      revenue: revenueByDate.get(entry.date) ?? 0,
      ranges: participationCountKeys.map((count) => {
        const bucket = entry.counts[String(count)];
        return bucket ? { count: bucket.count, rate: bucket.rate } : { count: 0, rate: 0 };
      }),
    }));
  }, [selectedItem, participationCountKeys]);

  const dailyCountColumnLabels = useMemo(
    () => participationCountKeys.map((count) => `${count}회`),
    [participationCountKeys],
  );

  return (
    <Box>
      {/* ── 상단 필터 영역 */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          gap: { xs: 1.5, md: 0 },
        }}
      >
        <Box sx={{ width: { xs: '100%', md: 'auto' } }}>
          <LotteryWeekSelector
            startDate={startDate}
            endDate={endDate}
            onChange={(start, end) => {
              setStartDate(start);
              setEndDate(end);
            }}
          />
        </Box>

        <Box
          sx={{ ml: { xs: 0, md: 3 }, mt: { xs: 1.5, md: 0 }, width: { xs: '100%', md: 'auto' } }}
        >
          <FilterSelectorWithApp
            selectedApp={appName}
            appOptions={appOptions}
            onAppChange={(v) => setAppName(v ?? '앱 전체')}
            onSearch={fetchData}
            selectedOs={selectedOs}
            onOsChange={setSelectedOs}
          />
        </Box>
      </Box>

      {/* ── 본문 카드 */}
      <Box sx={PAGE_CARD_SX}>
        {/* 앱 필터 Chip */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            overflowX: 'auto',
            pb: 2,
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#d1d5db', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9ca3af' },
          }}
        >
          {appOptions.map((name) => (
            <Chip
              key={name}
              label={name}
              onClick={() => setAppName(name)}
              sx={{
                cursor: 'pointer',
                fontWeight: appName === name ? 700 : 400,
                backgroundColor: appName === name ? '#0044D2' : '#fff',
                color: appName === name ? '#fff' : '#374151',
                border: `1px solid ${appName === name ? '#0044D2' : '#e5e7eb'}`,
                '&:hover': {
                  backgroundColor: appName === name ? '#0033a0' : '#f3f4f6',
                },
                height: 36,
                fontSize: '0.875rem',
              }}
            />
          ))}
        </Stack>

        <Divider sx={{ borderColor: '#f1f5f9', mb: 2 }} />

        {/* 콘텐츠(sub_type) 탭 */}
        <Tabs
          value={selectedLabel ?? false}
          onChange={(_, newValue: string) => setSelectedLabel(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              minHeight: 48,
              color: 'rgba(0, 0, 0, 0.6)',
              fontWeight: 'normal',
              px: 2,
              py: 0.75,
              borderRadius: '8px',
              mr: 1,
              '&.Mui-selected': {
                backgroundColor: 'rgba(0, 68, 210, 0.1)',
                color: '#0044D2',
                fontWeight: 'bold',
              },
            },
            '& .MuiTabs-indicator': {
              display: 'none',
            },
          }}
        >
          {labels.map((label) => {
            const isFixed = CONTENTS_LABEL_FIXED_ORDER.includes(label);
            const isUnclassified = label === CONTENTS_LABEL_UNCLASSIFIED;
            const tabLabel = isFixed ? (
              label
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  lineHeight: 1.3,
                }}
              >
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ fontSize: 10, color: '#94a3b8' }}
                >
                  RC
                </Typography>
                <Typography component="span" sx={{ fontSize: 13, fontWeight: 'inherit' }}>
                  {isUnclassified ? '미분류' : label}
                </Typography>
              </Box>
            );
            return <Tab key={label} label={tabLabel} value={label} />;
          })}
        </Tabs>

        {/* ── 로딩 / 에러 / 콘텐츠 분기 */}
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400} gap={2}>
            <CircularProgress size={50} sx={{ color: '#0044D2' }} />
            <Typography variant="body1">데이터 로딩 중...</Typography>
          </Box>
        ) : errorMessage ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <Alert severity="error">{errorMessage}</Alert>
          </Box>
        ) : labels.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={200}>
            <Typography color="#94a3b8">데이터가 없습니다.</Typography>
          </Box>
        ) : (
          <Box sx={{ mt: 1 }}>
            <ContentsParticipationKpiCards data={kpiData} />

            <RouletteContentUserStatusChart data={userStatusChartData} />

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 3,
                mt: 3,
              }}
            >
              <Box
                sx={{
                  backgroundColor: '#ffffff',
                  borderRadius: 3,
                  p: 2,
                  border: '1px solid #f1f5f9',
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  mb={1.5}
                  color="#475569"
                  textAlign="center"
                  sx={{ fontSize: '0.95rem' }}
                >
                  일별 매출
                </Typography>
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart
                    data={dailyRevenueChartData}
                    margin={{ top: 8, right: 16, left: 4, bottom: 4 }}
                  >
                    <defs>
                      <linearGradient id="colorContentsRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0044D2" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#0044D2" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="dayLabel" tick={{ fontSize: 9 }} interval={0} />
                    <YAxis
                      tickFormatter={(v: number) =>
                        v >= 10000 ? `${(v / 10000).toFixed(0)}만` : v.toLocaleString('ko-KR')
                      }
                      tick={{ fontSize: 11 }}
                    />
                    <RechartsTooltip formatter={(value: number) => formatCurrency(value)} />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      stroke="#0044D2"
                      strokeWidth={2}
                      fill="url(#colorContentsRevenue)"
                      fillOpacity={1}
                      name="매출"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>

              <Box sx={{ border: '1px solid #f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                <RouletteContentDailyCountChart data={dailyCountChartData} />
              </Box>
            </Box>

            <RouletteContentDailyCountTable
              columnLabels={dailyCountColumnLabels}
              rows={dailyCountTableRows}
              emphasize="count"
              showRevenue
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
